<template>
    <div class="bg-white rounded-xl shadow-lg p-6">
        <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-800">Program Statistics</h3>
            <div class="text-sm text-gray-500">Expected Graduates by Program</div>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div v-for="program in programs" :key="program.name"
                class="bg-gradient-to-br rounded-lg p-4 text-white transition-transform hover:scale-105"
                :class="program.gradientClass">
                <div class="flex items-center justify-between">
                    <div class="flex-1">
                        <p class="text-sm font-medium opacity-90">{{ program.name }}</p>
                        <p class="text-2xl font-bold mt-1">{{ formatNumber(program.count) }}</p>
                        <p class="text-xs opacity-75 mt-1">{{ ((program.count / totalGraduates) * 100).toFixed(1) }}% of
                            total</p>
                    </div>
                    <div class="bg-white bg-opacity-20 rounded-full p-3 ml-3">
                        <font-awesome-icon :icon="program.icon" class="text-lg" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ProgramStatisticsCards",
    props: {
        data: {
            type: Array,
            required: true,
            default: () => []
        }
    },
    computed: {
        programs() {
            // Calculate total count for each program across all campuses
            const programTotals = {};

            this.data.forEach(campus => {
                campus.programs.forEach(program => {
                    const programName = program.program_name;
                    if (!programTotals[programName]) {
                        programTotals[programName] = 0;
                    }
                    programTotals[programName] += program.expected_graduate_count;
                });
            });

            // Convert to array and sort by count (descending)
            const sortedPrograms = Object.entries(programTotals)
                .map(([name, count]) => ({
                    name,
                    count,
                    icon: this.getProgramIcon(name),
                    gradientClass: this.getProgramGradient(name)
                }))
                .sort((a, b) => b.count - a.count);

            return sortedPrograms;
        },
        totalGraduates() {
            return this.programs.reduce((total, program) => total + program.count, 0);
        }
    },
    methods: {
        formatNumber(num) {
            return num.toLocaleString();
        },
        getProgramIcon(programName) {
            const iconMap = {
                'BSc in Nursing': ['fas', 'user-nurse'],
                'Emergency Health (Paramedics)': ['fas', 'ambulance'],
                'Radiography & Medical Imaging': ['fas', 'x-ray'],
                'Physiotherapy': ['fas', 'dumbbell'],
                'Diploma in Emergency Health': ['fas', 'ambulance'],
                'Pharmacy': ['fas', 'pills'],
                'Health Care Assistant NQC': ['fas', 'user-md'],
                'Psychology': ['fas', 'brain'],
                'BSc in Midwifery': ['fas', 'baby']
            };
            return iconMap[programName] || ['fas', 'graduation-cap'];
        },
        getProgramGradient(programName) {
            const gradientMap = {
                'BSc in Nursing': 'from-pink-500 to-pink-600',
                'Emergency Health (Paramedics)': 'from-red-500 to-red-600',
                'Radiography & Medical Imaging': 'from-blue-500 to-blue-600',
                'Physiotherapy': 'from-indigo-500 to-indigo-600',
                'Diploma in Emergency Health': 'from-orange-500 to-orange-600',
                'Pharmacy': 'from-green-500 to-green-600',
                'Health Care Assistant NQC': 'from-teal-500 to-teal-600',
                'Psychology': 'from-purple-500 to-purple-600',
                'BSc in Midwifery': 'from-yellow-500 to-yellow-600'
            };
            return gradientMap[programName] || 'from-gray-500 to-gray-600';
        }
    }
};
</script>

<style scoped>
/* Component-specific styles can be added here */
</style>